import React from 'react';
import { ArrowLeft, FileText, Mail, Shield, Users, Sparkles, Scale, AlertCircle, Globe, RefreshCw, MessageCircle, Copyright, Link2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const Terms = () => {
  const sections = [
    {
      id: 'eligibility',
      title: '1. Eligibility',
      icon: Shield,
      content: 'GoalHive is intended only for users 18 years and older. By using GoalHive, you confirm you meet this requirement.'
    },
    {
      id: 'account',
      title: '2. Your Account',
      icon: Users,
      list: [
        'You are responsible for maintaining the confidentiality of your login information.',
        'You agree to provide accurate information and not impersonate anyone.'
      ]
    },
    {
      id: 'acceptable-use',
      title: '3. Acceptable Use',
      icon: AlertCircle,
      content: 'You agree not to:',
      list: [
        'Post unlawful, harassing, or harmful content.',
        'Use GoalHive to spam, phish, or engage in unsolicited promotions.',
        'Attempt to hack, disrupt, or interfere with the platform or other users.'
      ]
    },
    {
      id: 'user-content',
      title: '4. User Content',
      icon: FileText,
      content: 'You retain ownership of all content you post on GoalHive (goals, photos, videos, comments, and other materials). However, by posting content, you represent and warrant that:',
      list: [
        '<strong>Ownership:</strong> You own or have obtained all necessary rights, licenses, consents, and permissions to post and share your content on GoalHive.',
        '<strong>No Infringement:</strong> Your content does not and will not infringe, violate, or misappropriate any third party\'s intellectual property rights, privacy rights, publicity rights, or other personal or proprietary rights.',
        '<strong>License to GoalHive:</strong> You grant GoalHive a non-exclusive, worldwide, royalty-free, sublicensable license to use, display, reproduce, and distribute your content solely to operate, promote, and improve our Services. This license includes allowing other users to view and interact with your content based on your privacy settings.',
        '<strong>Original Content Only:</strong> You may only post content that you created yourself or have explicit permission to use. Do not post copyrighted material from third parties without authorization.',
        '<strong>Content Removal:</strong> We may remove any content that violates these Terms, our Community Guidelines, or applicable law.',
        '<strong>Prohibited Content:</strong> You may not post content containing: hate speech, harassment, threats, graphic violence, sexual exploitation, child endangerment, promotion of terrorism or illegal activities, spam, or misinformation that could cause harm.'
      ],
      note: '<strong>Reporting Violations:</strong> To report content that violates these Terms, contact support@goalhive.com with details of the violation. We review reports within 24-48 hours and will take appropriate action, including content removal and account suspension or termination for serious or repeated violations.'
    },
    {
      id: 'intellectual-property',
      title: '5. Intellectual Property & Copyright Policy',
      icon: Copyright,
      content: 'GoalHive respects the intellectual property rights of others and expects users to do the same. We comply with the Digital Millennium Copyright Act (DMCA) and will respond to valid notices of copyright infringement.',
      subsections: [
        {
          title: 'Prohibited Content',
          description: 'You may not post, share, upload, or distribute copyrighted music, videos, audio, movies, TV shows, or other copyrighted material from third-party sources (such as Spotify, Apple Music, YouTube, Netflix, etc.) without explicit authorization from the copyright owner.'
        },
        {
          title: 'What You Can Post',
          description: 'You may post original content you create yourself, including your own photos, videos, text, and audio recordings related to your personal goals and progress.'
        },
        {
          title: 'DMCA Takedown Notices',
          description: 'If you believe content on GoalHive infringes your copyright, send a written notice to our designated DMCA agent at dmca@goalhive.com including: (1) your signature, (2) identification of the copyrighted work, (3) identification of the infringing material and its location, (4) your contact information, (5) a statement of good faith belief, and (6) a statement under penalty of perjury that the information is accurate.'
        },
        {
          title: 'Repeat Infringer Policy',
          description: 'GoalHive will terminate the accounts of users who repeatedly infringe the copyrights or intellectual property rights of others. We reserve the right to suspend or terminate accounts after a single instance of clear infringement.'
        }
      ],
      note: '<strong>DMCA Agent Contact:</strong><br/>Email: <a href="mailto:dmca@goalhive.com" class="underline">dmca@goalhive.com</a><br/>GoalHive LLC<br/>Attn: DMCA Agent<br/>For full DMCA procedures, including counter-notice requirements, contact our DMCA agent.'
    },
    {
      id: 'third-party-services',
      title: '6. Third-Party Services',
      icon: Link2,
      content: 'GoalHive integrates with certain third-party services for limited purposes only. We want to be clear about what we do and do not provide access to:',
      subsections: [
        {
          title: 'Authentication Services',
          description: 'Apple Sign-In and Google Sign-In: We integrate with Apple and Google authentication services to allow you to create and access your account using your existing credentials. When you use these services, we receive limited profile information (name, email, profile photo) as permitted by those platforms.'
        },
        {
          title: 'YouTube Integration',
          description: 'YouTube Video Linking: Users may attach motivational or educational YouTube videos to their goals. We use YouTube\'s official embed player (iframe) for video playback, YouTube\'s public thumbnail service for previews, and provide an in-app browser to youtube.com to help users discover content. When you select a video, we save the YouTube URL to your goal. Videos play in YouTube\'s official player with all YouTube branding and controls intact. We do not download, cache, or modify YouTube content. Your use of YouTube content is subject to YouTube\'s Terms of Service.'
        },
        {
          title: 'What We Do NOT Do',
          description: 'We do not provide unauthorized access to third-party services. We do not download, convert, or extract content from YouTube or other streaming platforms. We do not remove branding, bypass paywalls, or circumvent content protections. We do not provide access to premium services (Spotify, Apple Music, Netflix, etc.) without proper authorization.'
        },
        {
          title: 'Your Responsibility',
          description: 'When you attach YouTube videos or other third-party content to your goals, you are responsible for complying with those services\' terms of use. You must have the right to share or reference any third-party content you include in GoalHive. We are not responsible for third-party services or content, and we do not guarantee their availability or accuracy.'
        },
        {
          title: 'Analytics & Infrastructure',
          description: 'We use third-party service providers for hosting, analytics, cloud storage, and customer support as described in our Privacy Policy. These services process data on our behalf under data protection agreements and do not provide end-user features or content access.'
        }
      ]
    },
    {
      id: 'ai-features',
      title: '7. AI Features',
      icon: Sparkles,
      content: 'GoalHive uses artificial intelligence ("AI") tools to suggest goals, track progress, provide insights, or personalize your experience. These features are for informational and motivational purposes only.',
      subsections: [
        {
          title: 'No Guarantee of Accuracy',
          description: 'AI outputs may be incomplete or inaccurate.'
        },
        {
          title: 'Not Professional Advice',
          description: 'AI content is not medical, mental health, fitness, financial, or legal advice.'
        },
        {
          title: 'User Responsibility',
          description: 'You are solely responsible for how you use AI outputs.'
        },
        {
          title: 'Data Use',
          description: 'AI features may process the information you share with GoalHive as outlined in our Privacy Policy.'
        }
      ]
    },
    {
      id: 'termination',
      title: '8. Termination',
      icon: RefreshCw,
      content: 'We may suspend or terminate your account if you violate these Terms, our policies, or engage in copyright infringement. Repeat copyright infringers will have their accounts permanently terminated. You may delete your account at any time through your account settings in the app or by contacting support@goalhive.com. Upon deletion, your personal data will be permanently removed immediately, though some data may persist in backups for up to 30 days. This action cannot be undone.'
    },
    {
      id: 'disclaimers',
      title: '9. Disclaimers & Limitation of Liability',
      icon: Scale,
      content: 'GoalHive is provided "as is" and "as available," without warranties of any kind. To the fullest extent permitted by law, GoalHive and its officers, directors, employees, and affiliates will not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Services. Our total liability will not exceed the amount you paid (if any) to use GoalHive.'
    },
    {
      id: 'changes',
      title: '10. Changes to These Terms',
      icon: FileText,
      content: 'We may update these Terms from time to time. If we make material changes, we\'ll notify you through the app or by email. Continued use after notice means you accept the updated Terms.'
    },
    {
      id: 'governing-law',
      title: '11. Governing Law',
      icon: Globe,
      content: 'These Terms are governed by the laws of Delaware, USA, without regard to its conflict of laws principles.'
    },
    {
      id: 'arbitration',
      title: '12. Dispute Resolution and Arbitration',
      icon: Scale,
      content: '<strong>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.</strong>',
      subsections: [
        {
          title: 'A. Informal Resolution',
          description: 'Before filing a claim, you agree to try to resolve the dispute informally by contacting us at support@goalhive.com. We\'ll try to resolve the dispute informally by contacting you via email. If a dispute is not resolved within 60 days of submission, you or GoalHive may bring a formal proceeding.'
        },
        {
          title: 'B. Binding Arbitration',
          description: 'You and GoalHive agree that any dispute, claim, or controversy arising out of or relating to these Terms or your use of the Services will be settled by binding arbitration, except that each party retains the right to seek injunctive relief in court for intellectual property infringement.'
        },
        {
          title: 'C. Arbitration Rules',
          description: 'The arbitration will be administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules. The arbitration will be conducted in Delaware, or remotely via video conference if you reside outside the United States.'
        },
        {
          title: 'D. Class Action Waiver',
          description: 'YOU AND GOALHIVE AGREE THAT EACH PARTY MAY BRING CLAIMS ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING. The arbitrator may not consolidate more than one person\'s claims.'
        },
        {
          title: 'E. Opt-Out Right',
          description: 'You have the right to opt out of binding arbitration within 30 days of first accepting these Terms by sending written notice to support@goalhive.com with subject line "ARBITRATION OPT-OUT." Your notice must include your name, address, username, and a clear statement that you want to opt out.'
        },
        {
          title: 'F. Severability',
          description: 'If any provision of these Terms is found invalid or unenforceable, the remaining provisions will remain in effect.'
        }
      ]
    },
    {
      id: 'indemnification',
      title: '13. Indemnification',
      icon: Shield,
      content: 'You agree to defend, indemnify, and hold harmless GoalHive LLC, its officers, directors, employees, agents, affiliates, and service providers from and against any claims, damages, losses, liabilities, costs, and expenses (including attorneys\' fees) arising from:',
      list: [
        'Your use of and access to the Services',
        'Your violation of any term of these Terms',
        'Your violation of any third-party right, including intellectual property or privacy rights',
        'Any claim that your User Content caused damage to a third party',
        'Your violation of any law or regulation',
        'Your use of third-party services (including YouTube) in violation of their terms'
      ],
      note: 'This obligation will survive termination of these Terms. GoalHive reserves the right to assume exclusive defense and control of any matter subject to indemnification by you.'
    },
    {
      id: 'force-majeure',
      title: '14. Force Majeure',
      icon: AlertCircle,
      content: 'GoalHive will not be liable for any delay or failure to perform resulting from causes outside its reasonable control, including:',
      list: [
        'Acts of God, natural disasters, war, terrorism, riots, or acts of civil or military authorities',
        'Fire, floods, earthquakes, accidents, or pandemics',
        'Strikes or shortages in transportation, fuel, energy, labor, or materials',
        'Failure of telecommunications or information services',
        'Cyberattacks, DDoS attacks, or other malicious attacks',
        'Actions or inactions of third-party service providers (e.g., hosting, cloud infrastructure)'
      ],
      note: 'In the event of a force majeure event, GoalHive\'s obligations will be suspended for the duration of such event. We will make reasonable efforts to notify you and resume performance as soon as practicable.'
    },
    {
      id: 'contact',
      title: '15. Contact Us',
      icon: MessageCircle,
      content: 'For questions about these Terms, contact us at',
      email: 'support@goalhive.com',
      note: 'For copyright-related inquiries or DMCA notices, please email <a href="mailto:dmca@goalhive.com" class="underline">dmca@goalhive.com</a>'
    }
  ];

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#0a0a0a]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#e5e5e5]/50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5">
              <img src="/image.webp" alt="GoalHive" className="w-8 h-8 object-contain" />
              <span className="text-lg font-semibold tracking-tight text-[#0a0a0a]">GoalHive</span>
            </Link>
            <div className="hidden md:flex items-center">
              <div className="flex items-center gap-1 p-1 bg-[#f5f5f5] rounded-full">
                <Link to="/support" className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#0a0a0a] hover:bg-white rounded-full transition-all duration-200">Support</Link>
                <Link to="/privacy" className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#0a0a0a] hover:bg-white rounded-full transition-all duration-200">Privacy</Link>
                <Link to="/terms" className="px-4 py-2 text-sm font-medium bg-white text-[#0a0a0a] rounded-full shadow-sm transition-all duration-200">Terms</Link>
              </div>
            </div>
            <Link
              to="/"
              className="h-10 px-5 bg-[#0a0a0a] text-white text-sm font-medium rounded-full hover:bg-[#171717] hover:scale-105 transition-all duration-200 shadow-lg shadow-black/10 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-14 h-14 bg-[#0a0a0a] rounded-2xl flex items-center justify-center">
              <FileText className="w-7 h-7 text-white" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
            <span className="text-[#0a0a0a]">Terms of </span>
            <span className="text-[#0a0a0a]">Service</span>
          </h1>
          <p className="text-xl text-[#666] mb-4">
            Please read these terms carefully before using GoalHive.
          </p>
          <p className="text-sm text-[#999]">
            <strong className="text-[#0a0a0a]">Effective Date:</strong> February 1, 2025
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="p-8 rounded-2xl bg-white border border-[#e5e5e5]">
            <p className="text-lg text-[#666] leading-relaxed">
              Welcome to GoalHive! These Terms of Service ("Terms") govern your use of the GoalHive mobile app and related services ("Services"). By creating an account, tapping "I Agree," or using GoalHive, you agree to these Terms and our Privacy Policy.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 pb-16">
        <div className="space-y-6">
          {sections.map((section) => {
            const IconComponent = section.icon;
            return (
              <div key={section.id} id={section.id} className="scroll-mt-24">
                <div className="rounded-2xl p-8 bg-white border border-[#e5e5e5] hover:border-[#d4d4d4] hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#0a0a0a] rounded-xl flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-[#0a0a0a] mb-4">
                        {section.title}
                      </h2>

                      {/* Content */}
                      {section.content && (
                        <p className="text-[#666] leading-relaxed mb-4">
                          <span dangerouslySetInnerHTML={{ __html: section.content }} />
                          {section.email && (
                            <a
                              href={`mailto:${section.email}`}
                              className="text-[#0a0a0a] hover:text-[#666] underline transition-colors ml-1"
                            >
                              {section.email}
                            </a>
                          )}
                        </p>
                      )}

                      {/* List Items */}
                      {section.list && (
                        <ul className="space-y-3">
                          {section.list.map((item, i) => (
                            <li key={i} className="flex items-start space-x-3">
                              <div className="w-1.5 h-1.5 bg-[#0a0a0a] rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-[#666]" dangerouslySetInnerHTML={{ __html: item }} />
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Subsections */}
                      {section.subsections && (
                        <div className="grid sm:grid-cols-2 gap-4 mt-4">
                          {section.subsections.map((subsection, i) => (
                            <div key={i} className="p-4 rounded-xl bg-[#fafafa] border border-[#e5e5e5]">
                              <h4 className="font-semibold text-[#0a0a0a] mb-2">{subsection.title}</h4>
                              <p className="text-[#666] text-sm">{subsection.description}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Note */}
                      {section.note && (
                        <div className="bg-[#f5f5f5] border-l-4 border-[#0a0a0a] rounded-r-lg p-4 mt-4">
                          <p className="text-sm text-[#666]" dangerouslySetInnerHTML={{ __html: section.note }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Contact CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="p-12 rounded-3xl bg-[#0a0a0a]">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-3xl font-bold mb-4 text-white">
              Questions About Our Terms?
            </h3>
            <p className="text-lg mb-8 text-white/70">
              We're happy to clarify any part of our terms of service.
            </p>
            <a
              href="mailto:support@goalhive.com"
              className="inline-block px-8 py-4 bg-white text-[#0a0a0a] font-semibold rounded-full hover:bg-[#f5f5f5] hover:scale-105 transition-all duration-200 shadow-lg"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2.5">
              <img src="/image.webp" alt="GoalHive" className="w-8 h-8 object-contain" />
              <div>
                <span className="text-lg font-semibold tracking-tight text-[#0a0a0a]">GoalHive</span>
                <p className="text-[#999] text-sm">&copy; 2025 GoalHive LLC. All rights reserved.</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-[#666]">
              <Link to="/support" className="hover:text-[#0a0a0a] transition-colors">Support</Link>
              <Link to="/privacy" className="hover:text-[#0a0a0a] transition-colors">Privacy Policy</Link>
              <Link to="/" className="hover:text-[#0a0a0a] transition-colors">Home</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
